import { initialPlayer, useStore } from "utils/store";
import { BuyIn, PokerAction, Street, StreetDetails } from "types";
import { useCallback, useState } from "react";
import { StorageValue } from "zustand/middleware";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export * from "./maintenance";

let _handId = -1;
let _bigBlind = 0;

export const useShuffle = () => {
  const reset = useStore((state) => state.reset);

  const shuffle = () => {
    _handId = -1;
    _bigBlind = 0;
    reset();
  };

  return shuffle;
};

export const useSkip = () => {
  const skip = useStore((state) => state.skip);
  return skip;
};

export const usePlayers = () => {
  const players = useStore((state) => state.players);
  const playerId = useStore((state) => state.playerId);
  const playerIds = Object.keys(players).filter(id => !!players[id].action);
  if (!!playerIds.length) {
    playerIds.splice(playerIds.indexOf(playerId), 1);
    playerIds.unshift(playerId);
  }

  return {
    playerIds,
    players,
  };
};

export const useHands = () => {
  const players = useStore((state) => state.players);
  const playerId = useStore((state) => state.playerId);
  const hands = useStore((state) => state.hands);
  const handCount = useStore((state) => state.handCount);
  const show = useStore((state) => state.showHandId);
  const summary = useStore((state) => state.summary);
  const handIds = !hands.length
    ? ""
    : hands
      .filter((currentHand) => !!currentHand)
      .map((currentHand) => currentHand.handNo + 1)
      .join();
  const currentHand = hands[handCount];

  if (show) _handId = hands[handCount]?.handNo ?? -1;
  if (players[playerId]?.time === 1)
    _bigBlind = currentHand.bigBlind;
  else if (!_bigBlind)
    _bigBlind = summary?.startingBigBlind || 0;

  return {
    hands,
    handIds,
    currentHand,
    currentHandId: _handId + 1,
    bigBlind: _bigBlind,
  };
};

export const useTournamentDetails = () => {
  const { currentHand } = useHands();
  const tournamentId = useStore((state) => state.tournamentId);
  const buyIn = useStore((state) => state.buyIn);
  const buyIns = useStore((state) => state.buyIns);

  const tournamentSize = useStore((state) => state.tournamentSize);
  const summary = useStore((state) => state.summary);
  const hands = useStore((state) => state.hands);
  const hand = currentHand || hands[0];
  const date = useStore((state) => state.date);
  const gameState = useStore((state) => state.gameState);
  const { players, playerIds } = usePlayers();
  const stats = useStore((state) => state.stats);

  const getBountyPlayer = (id: string) => {
    const bountyPlayer = hand?.tournamentDetails.bountyPlayerDetails.find(
      (p) => p.playerId === id
    );
    return {
      ...bountyPlayer,
      openingBounty:
        Number(
          currentHand ? bountyPlayer?.openingBounty : summary?.startingBounty
        ) || 0,
      bounty: Number(bountyPlayer?.bounty) || 0,
      openingBank: Number(bountyPlayer?.openingBank) || 0,
      bank: Number(bountyPlayer?.bank) || 0,
    };
  };

  const winnerId = playerIds.reduce((prevId, nextId) => {
    const next = players[nextId];
    if (Number(next.winnings) > Number(players[prevId].winType)) return nextId;
    return prevId;
  }, playerIds[0]);

  return {
    bankLeader: {
      ...hand?.tournamentDetails.bankLeader,
      openingBank: Number(hand?.tournamentDetails.bankLeader.openingBank) || 0,
    },
    bountyLeaderboard:
      hand?.tournamentDetails.bountyLeaderboard.map((b) => ({
        ...b,
        bounty: Number(b.bounty),
      })) || [],
    getBountyPlayer,
    tournamentId,
    buyIn,
    tournamentSize,
    date,
    buyIns,
    gameState,
    winType: players[winnerId]?.winType || initialPlayer.winType,
    lowestBounty: stats?.minStartingBounty || 0,
    highestBounty: stats?.maxStartingBounty || 0,
  };
};

export const usePot = () => {
  const { players } = usePlayers();
  const { currentHand } = useHands();
  let totalBet = 0;
  const ante =
    currentHand?.roundDetails[PokerAction.Ante].reduce(
      (total: number, stage: StreetDetails) => total + Number(stage.amount),
      0
    ) || 0;

  Object.values(players).forEach((player) => {
    totalBet += player.amount;
  });

  return totalBet + ante;
};

export const usePlayer = (id?: string) => {
  const { players, playerIds } = usePlayers();
  const heroId = useStore((state) => state.playerId);
  const _id = id || heroId;
  const { getBountyPlayer } = useTournamentDetails();
  const bountyPlayer = getBountyPlayer(_id);
  const { currentHand } = useHands();
  const currentId = useStore((state) => state.currentId);

  const i = currentId === undefined ? -1 : playerIds.indexOf(currentId) + 1;
  const holeCards =
    playerIds.indexOf(_id) <= i && !!players[_id] ? players[_id].holeCards : [];

  const action = players[_id]?.action || initialPlayer.action;
  const time = players[_id]?.time || 0;
  const winnings = Number(players[_id]?.winnings) || 0;
  const amount = players[_id]?.amount || 0;
  const balance = (players[_id]?.balance || 0) + (time < 0 ? winnings : amount * -1);
  const isBust = players[_id]?.balance + winnings === 0 && (_id !== heroId || time < 0);

  const showBounty = useStore((state) => state.showBounty);
  const bounty =
    showBounty && _id === heroId
      ? bountyPlayer?.bounty
      : bountyPlayer?.openingBounty || 0;
  const bountyWinnings = bounty - bountyPlayer.openingBounty;

  const showBank = useStore((state) => state.showBank);
  const bank = showBank ? bountyPlayer?.bank : bountyPlayer?.openingBank || 0;

  const leaderboard = currentHand?.tournamentDetails.bountyLeaderboard || [];

  return {
    balance,
    winnings,
    amount,
    equity: players[_id]?.equity,
    action,
    time,
    index: playerIds.indexOf(_id),
    holeCards,
    bounty,
    bank,
    hunting: bountyPlayer?.hunting || 0,
    huntingMe: bountyPlayer?.huntingMe || 0,
    bountyWinnings,
    topPlayer: _id !== heroId && leaderboard.some((value) => value.playerId === _id),
    isBust,
    isActive: !!holeCards.length && action !== PokerAction.Fold && !isBust
  };
};

export const useSummary = (forceShow?: boolean) => {
  const summary = useStore((state) => state.summary);
  const player = usePlayer();
  const { currentHand } = useHands();
  const firstFlushDetails = summary?.firstFlush;
  const secondFlushDetails = summary?.secondFlush;
  let firstFlush = "";
  let secondFlush = "";


  if (forceShow) {
    firstFlush = firstFlushDetails?.flushType ?? "";
    secondFlush = secondFlushDetails?.flushType ?? "";
  } else if (currentHand) {
    if (!!firstFlushDetails) {
      if (
        (firstFlushDetails.handNo === currentHand.handNo &&
          player.winnings !== 0) ||
        firstFlushDetails.handNo < currentHand.handNo
      ) {
        firstFlush = firstFlushDetails.flushType;
      }
    }

    if (!!secondFlushDetails) {
      if (
        (secondFlushDetails.handNo === currentHand.handNo &&
          player.winnings !== 0) ||
        secondFlushDetails.handNo < currentHand.handNo
      ) {
        secondFlush = secondFlushDetails.flushType;
      }
    }
  }

  return {
    ...summary,
    startingBounty: Number(summary?.startingBounty || 0),
    startingBalance: Number(summary?.startingBalance || 0),
    totalJackpotWon: Number(summary?.totalJackpotWon || 0),
    totalBountyWon: Number(summary?.totalBountyWon || 0),
    totalWinnings: Number(summary?.totalWinnings || 0),
    firstFlush,
    secondFlush,
    firstFlushElapsed:
      !!firstFlushDetails && currentHand
        ? currentHand.handNo > firstFlushDetails.handNo
        : false,
    secondFlushElapsed:
      !!secondFlushDetails && currentHand
        ? currentHand.handNo > secondFlushDetails.handNo
        : false,
  };
};

export const useCards = () => {
  const { currentHand } = useHands();
  const heroId = useStore((state) => state.playerId);
  const heroFolds = useAction(heroId, PokerAction.Fold);
  const street = useStore((state) => state.street);
  let len = 0;
  switch (street) {
    case Street.Flop:
      len = 3;
      break;
    case Street.Turn:
      len = 4;
      break;
    case Street.River:
      len = 5;
      break;
  }

  return {
    communityCards: currentHand?.communityCards.slice(0, len) || [],
    isLastCard: currentHand?.communityCards.length <= len || heroFolds,
  };
};

export const useJackpots = () => {
  const jackpotDetails = useStore((state) => state.jackpotDetails);
  let jackpots: number[] = [];
  if (jackpotDetails) {
    jackpots = Object.values(jackpotDetails).map((value) => Number(value));
  }
  return jackpots;
};

export const useAction = (id: string, _action: PokerAction) => {
  const hideState = useStore((state) => state.hideState);
  const { action } = usePlayer(id);
  if (_action !== action || (action === PokerAction.AllIn && !!hideState)) return false;
  return true;
};

export const usePrizes = () => {
  const show = useStore((state) => state.showPrize);
  const { secondFlush, secondFlushElapsed } = useSummary(false);
  const id = useStore((state) => state.playerId);
  const player = usePlayer();
  const { bigBlind } = useHands();
  const { getBountyPlayer } = useTournamentDetails();
  const pot = usePot();
  const bountyPlayer = getBountyPlayer(id);
  const isBountyWin =
    show && bountyPlayer && bountyPlayer.bounty > bountyPlayer.openingBounty;
  const isJackpotWin = show && !!secondFlush.trim().length && !secondFlushElapsed;

  return {
    isJackpotWin,
    isBountyWin,
    isBigWin: show && player.winnings > 0 && pot / bigBlind >= 30 && !isJackpotWin && !isBountyWin,
    isBigLose: show && player.winnings / bigBlind <= -30,
  };
};

export const useCurrency = () => {
  const currency = useStore((state) => state.currencyCode);

  const parseCurrency = (s: string | number, forceDecimal?: boolean) => {
    const n = Number(s);
    if (isNaN(n)) return s.toString();

    if (n < 1 && n > 0) {
      return Math.floor(n * 100) + "¢";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: n % 1 !== 0 || forceDecimal ? 2 : 0,
    }).format(n);
  };

  return {
    parseCurrency,
  };
};

export const useStorage = () => {
  const storage = useStore.persist.getOptions().storage?.getItem("bounty-hunter");
  const { state } = storage as StorageValue<{
    buyIn: BuyIn;
    gameId: string;
    sessionId: string;
    returnUrl: string;
  }>;
  return state;
};

export const useGame = () => {
  const { buyIns } = useTournamentDetails();
  const setBuyIn = useStore((state) => state.setBuyIn);
  const _setGame = useStore((state) => state.setGame);
  const _setBet = useStore((state) => state.setBet);
  const shuffle = useShuffle();
  const navigate = useNavigate();
  const { show: showNoTicketAlert } = useNoTicketAlert();
  const storage = useStorage();
  const setShowInsufficient = useStore((state) => state.setShowInsufficient);


  const setGame = useCallback((gameId?: string, sessionId?: string) => {
    shuffle();
    _setGame(gameId || storage.gameId, sessionId || storage.sessionId);
  }, [storage, _setGame, shuffle]);

  const setBet = useCallback(() => {
    _setBet().then(result => {
      if (!result) {
        navigate("/lobby");
      }
    }).catch((error) => {
      if (axios.isAxiosError(error) && error.response?.data?.errorCode === "ERR_LOW_BALANCE") {
        setShowInsufficient(true);
        return;
      }

      showNoTicketAlert();
    });
  }, [_setBet, buyIns, setBuyIn, showNoTicketAlert]);

  return {
    setGame,
    setBet,
  };
};

export const useForceUpdate = () => {
  const [, setUpdate] = useState({});
  const forceUpdate = useCallback(() => setUpdate({}), []);

  return {
    forceUpdate
  }
};

export const useAlertMessage = () => {
  const message = useStore((state) => state.alertMessage);
  const setMessage = useStore((state) => state.setAlertMessage);
  const alert = useCallback(
    (message: string) => setMessage(message),
    [setMessage]
  );
  const clear = useCallback(() => {
    setMessage(undefined);
  }, [setMessage]);

  return {
    message,
    alert,
    clear,
  };
};

export const useNoTicketAlert = () => {
  const { alert, clear, message } = useAlertMessage();
  const show = useCallback(() => {
    alert("This ticket is currently unavailable.");
  }, [alert]);

  const hide = () => {
    clear();
  }

  return { show, hide, message };
};
